/**
 * Language Switcher Component
 *
 * Dropdown-based language selector for the header, supporting 7 locales.
 * Uses a translate icon + current locale label as trigger, opening an
 * animated floating dropdown with all language options.
 *
 * Urdu variants (Script + Roman) are visually grouped with a subtle
 * separator and group label for clarity.
 *
 * Data-driven from LANGUAGE_OPTIONS -- adding a new language only requires
 * a new entry in the array (plus the locale in SUPPORTED_LOCALES).
 */

import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Languages, ChevronDown } from 'lucide-react';
import { stripLocaleFromPath, type SupportedLocale } from './index';
import { SUPPORTED_LOCALES } from './locales.generated';
import { useAuth } from '../../src/hooks/useAuth';
import { profileApi } from '../../entities/user/services/profileApi';

/** Language option with optional group separator */
interface LanguageOption {
  locale: SupportedLocale;
  label: string;
  fullName: string;
  ariaLabel: string;
  /** If set, a group header is rendered before this option */
  groupLabel?: string;
}

/**
 * Language options displayed in the switcher — order matters for layout.
 *
 * Presentation-only data (label, fullName, ariaLabel) lives here, not in the
 * locale registry, because the registry is platform metadata and the switcher
 * UI should be free to evolve aesthetic choices independently.
 *
 * The runtime drift check below catches the one failure mode that matters: if
 * the registry adds a locale and this list doesn't, the new locale would be
 * silently invisible in the switcher.
 */
const LANGUAGE_OPTIONS: LanguageOption[] = [
  { locale: 'en', label: 'EN', fullName: 'English', ariaLabel: 'English' },
  { locale: 'tr', label: 'TR', fullName: 'Türkçe', ariaLabel: 'Türkçe' },
  { locale: 'es', label: 'ES', fullName: 'Español', ariaLabel: 'Español' },
  { locale: 'bn', label: 'বাং', fullName: 'বাংলা', ariaLabel: 'Bengali' },
  { locale: 'ar', label: 'عر', fullName: 'العربية', ariaLabel: 'العربية' },
  { locale: 'ur', label: 'اردو', fullName: 'اردو', ariaLabel: 'Urdu Nastaliq script', groupLabel: 'Urdu' },
  { locale: 'ur-Latn', label: 'UR', fullName: 'Urdu (Roman)', ariaLabel: 'Urdu Roman script' },
];

// Drift guard: warn (in dev) and fail-loud (in prod via console.error) if the
// registry exposes a locale the switcher doesn't render. Only catches the
// missing-here case; an extra entry here for a removed registry locale is a
// soft drift the user just won't see in the switcher.
{
  const switcherLocales = new Set(LANGUAGE_OPTIONS.map(o => o.locale));
  const missing = SUPPORTED_LOCALES.filter(code => !switcherLocales.has(code));
  if (missing.length > 0) {
    // eslint-disable-next-line no-console
    console.error(
      `LanguageSwitcher drift: registry exposes ${missing.join(', ')} but the switcher has no entry. ` +
        `Add a LANGUAGE_OPTIONS entry to keep the UI in sync with backend capabilities.`,
    );
  }
}

interface LanguageSwitcherProps {
  className?: string;
  /** 'dropdown' for desktop floating menu, 'inline' for mobile menu expansion */
  variant?: 'dropdown' | 'inline';
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className = '',
  variant = 'dropdown',
}) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { authState } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLocale = i18n.language as SupportedLocale;

  const currentOption = LANGUAGE_OPTIONS.find((opt) => opt.locale === currentLocale) || LANGUAGE_OPTIONS[0];

  const switchTo = useCallback(
    (locale: SupportedLocale) => {
      if (locale === currentLocale) return;

      const pathWithoutLocale = stripLocaleFromPath(location.pathname);
      const cleanPath = pathWithoutLocale === '/' ? '' : pathWithoutLocale;
      const newPath = `/${locale}${cleanPath}${location.search}${location.hash}`;

      i18n.changeLanguage(locale);
      navigate(newPath, { replace: true });
      setIsOpen(false);

      // Persist preferredLanguage for logged-in users — fire-and-forget so the
      // UX never blocks on the API. ur-Latn collapses to 'ur' because the backend's
      // translation pipeline has no Roman Urdu provider; their authored content
      // gets stamped as Urdu (Nastaliq) on write, and a one-time toast informs them.
      // Uses PATCH /profile/auto-save (the partial-update endpoint) — sending a
      // single field to PUT /profile fails Zod validation since that endpoint
      // expects a full personal-info update shape.
      if (authState.isAuthenticated) {
        const backendLang = locale === 'ur-Latn' ? 'ur' : locale;
        void profileApi.autoSaveProfile({ preferredLanguage: backendLang } as never).catch(() => {
          /* Silent — locale switch already succeeded client-side. */
        });
      }
    },
    [i18n, currentLocale, location, navigate, authState.isAuthenticated]
  );

  // Close dropdown on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close dropdown on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Inline variant for mobile menus -- expands within the flow, no floating dropdown
  if (variant === 'inline') {
    return (
      <div className={className}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between py-3 text-gray-700 dark:text-gray-300 transition-colors duration-200 font-jakarta font-medium"
          aria-label={t('accessibility.switchLanguage')}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <div className="flex items-center gap-2.5">
            <Languages className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />
            <span>{currentOption.fullName}</span>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div className="pb-2 space-y-0.5" role="listbox" aria-label={t('accessibility.switchLanguage')}>
            {LANGUAGE_OPTIONS.map((option) => {
              const isActive = option.locale === currentLocale;
              return (
                <React.Fragment key={option.locale}>
                  {option.groupLabel && (
                    <div className="px-3 pt-2 pb-1">
                      <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                        {option.groupLabel}
                      </span>
                    </div>
                  )}
                  <button
                    onClick={() => switchTo(option.locale)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors duration-150 ${
                      isActive
                        ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/20 font-semibold'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 font-medium'
                    }`}
                    role="option"
                    aria-selected={isActive}
                    aria-label={option.ariaLabel}
                  >
                    <span className="w-7 text-center text-xs font-semibold shrink-0">{option.label}</span>
                    <span>{option.fullName}</span>
                    {isActive && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger: icon + current language code */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1 h-10 px-2.5 rounded-full border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
        aria-label={t('accessibility.switchLanguage')}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Languages className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true" />
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
          {currentOption.label}
        </span>
      </button>

      {/* Floating dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute right-0 top-full mt-2 min-w-[160px] w-max rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg shadow-gray-200/50 dark:shadow-gray-900/50 py-1 z-50"
            role="listbox"
            aria-label={t('accessibility.switchLanguage')}
          >
            {/* Arrow pointer */}
            <div className="absolute -top-1.5 right-4 w-3 h-3 bg-white dark:bg-gray-800 border-l border-t border-gray-200 dark:border-gray-700 rotate-45" />

            {LANGUAGE_OPTIONS.map((option) => {
              const isActive = option.locale === currentLocale;
              return (
                <React.Fragment key={option.locale}>
                  {/* Group separator for Urdu variants */}
                  {option.groupLabel && (
                    <div className="px-3.5 pt-1.5 pb-0.5 mt-0.5 border-t border-gray-100 dark:border-gray-700/50">
                      <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                        {option.groupLabel}
                      </span>
                    </div>
                  )}
                  <button
                    onClick={() => switchTo(option.locale)}
                    className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm transition-colors duration-150 ${
                      isActive
                        ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/20 font-semibold'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 font-medium'
                    }`}
                    role="option"
                    aria-selected={isActive}
                    aria-label={option.ariaLabel}
                  >
                    <span className="w-7 text-center text-xs font-semibold shrink-0">{option.label}</span>
                    <span className="whitespace-nowrap">{option.fullName}</span>
                    {isActive && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
